import { FormikErrors } from "formik";
import _ from "lodash";
import React from "react";
import {
  FormSchema,
  FormType,
  getAllQuestionIds,
  getQuestionTypeFormValue,
  getVerificationFormValue,
} from "src/scenes/orgAdmin/formTemplates/ContentTab/V1/formik";
import { Question } from "src/services/formTemplate";
import { validateWithZod } from "src/services/formValidations";
import { isNonEmptyString } from "src/services/predicates";
import * as AF from "src/types/formTemplate";

type Props = {
  isV2FormTemplate: boolean;
  verificationOptions?: AF.FormVerification<AF.WithId>[];
};
export const useFormValidation = ({
  isV2FormTemplate,
  verificationOptions,
}: Props) => {
  const validate = React.useCallback(
    async (formValues: FormType) => {
      const errors: FormikErrors<FormType> = _.merge(
        await validateWithZod(FormSchema)(formValues),
        validateNewVerificationLabel(formValues, verificationOptions),
        validateOptions(formValues),
        isV2FormTemplate ? validateV2KeysAndValues(formValues) : {}
      );
      if (_.isEmpty(errors)) {
        return undefined;
      }

      return errors;
    },
    [isV2FormTemplate, verificationOptions]
  );

  return validate;
};

function validateOptions(formValues: FormType): FormikErrors<FormType> {
  const allQuestionIds = getAllQuestionIds(formValues);
  const validations = Object.fromEntries(
    allQuestionIds.flatMap((questionId) => {
      const questionType = getQuestionTypeFormValue(questionId, formValues);
      if (!Question.hasOptions({ type: questionType })) {
        return [];
      }

      return [];
    })
  );

  if (_.isEmpty(validations)) {
    return {};
  }

  return { validations };
}

const validateNewVerificationLabel = (
  formValues: FormType,
  verificationOptions?: AF.FormVerification<AF.WithId>[]
): FormikErrors<FormType> => {
  const allQuestionIds = getAllQuestionIds(formValues);
  const verifications = Object.fromEntries(
    allQuestionIds.flatMap((questionId) => {
      const verification = getVerificationFormValue(questionId, formValues);
      if (verification.type !== "new" || !verificationOptions) {
        return [];
      }

      const newLabel = verification.label.trim();
      const labelExists =
        verificationOptions.find((option) => option.label === newLabel) !==
        undefined;
      if (!labelExists) {
        return [];
      }

      return [
        [
          questionId,
          {
            label:
              "A new verification cannot have the same name as an existing verification",
          },
        ],
      ];
    })
  );

  if (_.isEmpty(verifications)) {
    return {};
  }
  return { verifications };
};

function validateV2KeysAndValues(formValues: FormType): FormikErrors<FormType> {
  const errors: FormikErrors<FormType> = {};

  // Validate question keys are required for v2
  Object.entries(formValues.questionKeys || {}).forEach(([questionId, key]) => {
    if (!isNonEmptyString(key)) {
      errors.questionKeys = {
        ...errors.questionKeys,
        [questionId]: "Question key is required",
      };
    }
  });

  // Validate option values are required for v2
  Object.entries(formValues.options || {}).forEach(([optionId, option]) => {
    if (!isNonEmptyString(option.value)) {
      errors.options = {
        ...errors.options,
        [optionId]: {
          ...errors.options?.[optionId],
          value: "Option value is required",
        },
      };
    }
  });

  return errors;
}
